import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  Users,
  Building,
  MessageSquare,
  Calendar,
  Mail,
  TrendingUp,
  AlertCircle,
  Crown,
  Zap
} from "lucide-react";
import { useAuth } from '@/contexts/auth-context';
import { useOrganizationUsage } from '@/hooks/api-hooks';

interface UsageItemProps {
  icon: React.ComponentType<any>;
  label: string;
  current: number;
  limit: number;
  percentage: number;
}

function UsageItem({
  icon: Icon,
  label,
  current,
  limit,
  percentage
}: UsageItemProps) {
  const getStatusColor = () => {
    if (percentage >= 90) return 'text-destructive';
    if (percentage >= 80) return 'text-warning';
    return 'text-success';
  };

  const getIconColor = () => {
    if (percentage >= 90) return 'text-destructive-foreground';
    if (percentage >= 80) return 'text-warning-foreground';
    return 'text-muted-foreground';
  };

  return (
    <div className="space-y-3 p-4 rounded-xl border border-border bg-background hover:bg-muted/30 transition-colors duration-200">
      {/* Header with icon and label */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-secondary/50 border border-border">
            <Icon className={`h-4 w-4 ${getIconColor()}`} />
          </div>
          <div className="space-y-1">
            <span className="text-sm font-medium text-foreground leading-none">{label}</span>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-semibold ${getStatusColor()}`}>
                {current} of {limit}
              </span>
              {percentage >= 80 && (
                <AlertCircle className="h-3 w-3 text-warning" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Progress section */}
      <div className="space-y-2">
        <Progress
          value={Math.min(percentage, 100)}
          className="h-2"
        />
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {percentage.toFixed(1)}% used
          </span>
          {percentage >= 90 && (
            <span className="text-xs text-warning font-medium">
              Approaching limit
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export function UsageDashboardWidget() {
  const { user } = useAuth()

  const currentOrganization = user?.organizations?.find((org: any) => org.id === user?.currentOrganizationId)
    ?? user?.organizations?.[0]

  const planName = (currentOrganization?.plan ?? 'free').toString().toUpperCase()
  const planDisplayName = planName.charAt(0) + planName.slice(1).toLowerCase()

  const organizationId = currentOrganization?.id
  const { data: usageResponse } = useOrganizationUsage(organizationId)
  const usagePayload = (usageResponse as { data?: any } | undefined)?.data

  const usageData = {
    planInfo: {
      name: (usagePayload?.plan ?? planName)?.toString().toUpperCase(),
      displayName: usagePayload?.planDisplayName ?? planDisplayName,
    },
    teamUsage: {
      current: usagePayload?.teams?.current ?? 0,
      limit: usagePayload?.teams?.limit ?? 0,
      percentage: usagePayload?.teams?.usagePercentage ?? 0,
    },
    memberUsage: {
      current: usagePayload?.members?.current ?? 0,
      limit: usagePayload?.members?.limit ?? 0,
      percentage: usagePayload?.members?.usagePercentage ?? 0,
    },
    feedbackCycleUsage: {
      current: usagePayload?.feedbackCycles?.current ?? 0,
      limit: usagePayload?.feedbackCycles?.limit ?? 0,
      percentage: usagePayload?.feedbackCycles?.usagePercentage ?? 0,
    },
    oneOnOneUsage: {
      current: usagePayload?.oneOnOnes?.current ?? 0,
      limit: usagePayload?.oneOnOnes?.limit ?? 0,
      percentage: usagePayload?.oneOnOnes?.usagePercentage ?? 0,
    },
    feedbackEmailUsage: {
      current: usagePayload?.feedbackEmails?.current ?? 0,
      limit: usagePayload?.feedbackEmails?.limit ?? 0,
      percentage: usagePayload?.feedbackEmails?.usagePercentage ?? 0,
    },
    lastUpdated: usagePayload?.updatedAt ? new Date(usagePayload.updatedAt) : null,
  };

  const usageBuckets = [
    usageData.teamUsage,
    usageData.memberUsage,
    usageData.feedbackCycleUsage,
    usageData.oneOnOneUsage,
    usageData.feedbackEmailUsage,
  ]

  const hasCritical = usageBuckets.some((item) => item.limit > 0 && item.percentage >= 100)
  const hasWarnings = !hasCritical && usageBuckets.some((item) => item.limit > 0 && item.percentage >= 80)

  return (
    <Card className="border-border bg-background">
      <CardHeader className="pb-6 space-y-0">
        <div className="flex items-center justify-between">
          {/* Header with icon and title */}
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 border border-primary/20">
              <TrendingUp className="h-6 w-6 text-primary" />
            </div>
            <div className="space-y-1">
              <CardTitle className="text-xl font-semibold text-foreground">Plan Usage</CardTitle>
              <CardDescription className="text-sm text-muted-foreground">
                Monitor your {usageData.planInfo.displayName} plan limits
              </CardDescription>
            </div>
          </div>

          {/* Plan badge */}
          {usageData.planInfo.name !== 'ENTERPRISE' && (
            <Badge
              variant="outline"
              className="gap-1.5 px-3 py-1 border-border bg-secondary/30 text-secondary-foreground"
            >
              <Crown className="h-3 w-3" />
              {usageData.planInfo.displayName}
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Usage Items Grid - Single Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <UsageItem
            icon={Building}
            label="Teams"
            current={usageData.teamUsage.current}
            limit={usageData.teamUsage.limit}
            percentage={usageData.teamUsage.percentage}
          />

          <UsageItem
            icon={Users}
            label="Team Members"
            current={usageData.memberUsage.current}
            limit={usageData.memberUsage.limit}
            percentage={usageData.memberUsage.percentage}
          />

          <UsageItem
            icon={MessageSquare}
            label="Feedback Cycles"
            current={usageData.feedbackCycleUsage.current}
            limit={usageData.feedbackCycleUsage.limit}
            percentage={usageData.feedbackCycleUsage.percentage}
          />

          <UsageItem
            icon={Calendar}
            label="1-on-1 Sessions"
            current={usageData.oneOnOneUsage.current}
            limit={usageData.oneOnOneUsage.limit}
            percentage={usageData.oneOnOneUsage.percentage}
          />

          <UsageItem
            icon={Mail}
            label="Feedback Emails"
            current={usageData.feedbackEmailUsage.current}
            limit={usageData.feedbackEmailUsage.limit}
            percentage={usageData.feedbackEmailUsage.percentage}
          />
        </div>

        {/* Action Section */}
        {(hasWarnings || hasCritical) && (
          <div className="border-t border-border pt-6 space-y-4">
            {hasCritical && (
              <div className="p-4 bg-destructive/5 border border-destructive/20 rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-destructive/10 border border-destructive/20 flex-shrink-0">
                    <AlertCircle className="h-4 w-4 text-destructive" />
                  </div>
                  <div className="flex-1 space-y-1">
                    <p className="text-sm font-semibold text-destructive">
                      Plan Limits Reached
                    </p>
                    <p className="text-xs text-destructive/80 leading-relaxed">
                      You've reached your plan limits. Upgrade to continue using these features.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-3">
              {usageData.planInfo.name !== 'ENTERPRISE' ? (
                <Button
                  size="sm"
                  className="w-full gap-2 h-10"
                >
                  <Zap className="h-4 w-4" />
                  Upgrade Plan
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full gap-2 h-10"
                >
                  Contact Sales
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        {usageData.lastUpdated && (
          <div className="border-t border-border pt-4">
            <p className="text-xs text-muted-foreground text-center">
              Last updated: {usageData.lastUpdated.toLocaleDateString()} at {usageData.lastUpdated.toLocaleTimeString()}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
